import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyEmail } from '../api/verifyEmail';
import { postVerifyDestination } from '../utils/returnUrl';

// bl-813 — the email-verification link landing page.
//
// identity-service emails a link to `{zone}/verify-email?token=…`. This page
// reads the `token` query param, POSTs it to identity-service `POST /verify-email`
// (via the api/verifyEmail.ts client, which mirrors api/register.ts), and routes
// the member onward into onboarding on success. It is a REAL route (App.tsx) so
// the link no longer falls through to the `*` ComingSoonPage catch-all.
//
// SECURITY: the token is read from the URL and passed straight to the API client
// in the request body — it is NEVER logged. Every server/token failure collapses
// to one uniform `error` state (non-enumerating).
//
// Per conventions/frontend-patterns.md, navigation uses <Link> and every state +
// interactive element carries a data-testid. Copy is functional/placeholder —
// voice + UX polish is a separate Menley story, OUT of bl-813.

type VerifyState = 'verifying' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();

  // Guard FIRST (AC5): a missing / empty / whitespace-only token short-circuits
  // to `error` WITHOUT ever calling the API. Trimmed so a `?token=%20` is empty.
  const token = useMemo(() => {
    const raw = searchParams.get('token');
    return raw && raw.trim() ? raw : null;
  }, [searchParams]);

  // The onward target is captured on mount from the SAME brand-default convention
  // the register/login flows use (no hardcoded host → zone-agnostic, AC1/AC4).
  //
  // bl-1322: the destination is BRAND-BRANCHED. The verify link is zone-aware
  // (identity-service `email::link_base` bases it on the registration request's
  // allowlisted Origin), so a FounderyOS registrant clicks through to a
  // founderyos.dev front-door host — and from there the continuation is the FOS
  // app, NOT the DAO portal's `/onboarding` Internet-Identity gate. A DAO
  // registrant still gets `<portal>/onboarding` (epic-onb-001, unchanged).
  const onwardTarget = useMemo(
    () => postVerifyDestination(searchParams.get('returnUrl')),
    [searchParams],
  );

  // Start in `error` when there is no usable token (the guard), else `verifying`.
  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'error');

  useEffect(() => {
    // No token → the guard already set `error`; never call the API.
    if (!token) {
      return;
    }

    let cancelled = false;
    void (async () => {
      // The token is passed straight to the client; never logged here.
      const result = await verifyEmail(token);
      if (cancelled) {
        return;
      }
      if (result.kind === 'success') {
        setState('success');
        // Route the member INTO onboarding (the portal II-link step). A full-page
        // assign because the portal is a cross-origin host.
        window.location.assign(onwardTarget);
        return;
      }
      setState('error');
    })();

    return () => {
      cancelled = true;
    };
  }, [token, onwardTarget]);

  return (
    <main className="register" data-testid="verify-email-page">
      <section className="register__card">
        <h1 className="register__title">Verify your email</h1>

        {state === 'verifying' && (
          <p
            className="register__subtitle"
            data-testid="verify-email-verifying"
            role="status"
          >
            Verifying your email…
          </p>
        )}

        {state === 'success' && (
          <p
            className="register__banner"
            data-testid="verify-email-success"
            role="status"
          >
            {/* bl-1322: brand-neutral. "onboarding" named the DAO's II-link
                step, which a FounderyOS member is no longer sent to — leaving the
                word here would ship DAO vocabulary into the FOS flow. Still
                functional/placeholder copy; voice is Menley's pass (AC6). */}
            Your email&apos;s verified. Taking you to your account…
          </p>
        )}

        {state === 'error' && (
          <div data-testid="verify-email-error">
            <p
              className="register__banner register__banner--block"
              role="alert"
            >
              We couldn&apos;t verify your email. The link may have expired or
              already been used.
            </p>
            <p className="register__footer">
              {/* Retry / resend affordance: head back to register to request a
                  fresh verification link. */}
              <Link to="/register" data-testid="verify-email-register-link">
                Back to register
              </Link>
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
